import {
  MobileEmbedMessages,
  MobileViewPath,
  getDocumentsPageUrl,
  getMobileViewUrls,
  getSettingsPageUrl
} from '@constants'
import {
  PermissionSet,
  useBusinessContext,
  useFeature,
  useMobileEmbed,
  usePermission,
  usePermissionsLoaded
} from '@context'
import { EnsureBankAccountSlide, PartnerSlide } from '@pages/BankIntegrationOnboardingPage/slides'
import { AspspsSlide } from '@pages/BankIntegrationOnboardingPage/slides/AspspsSlide'
import { showCompleteToast } from '@pages/BankIntegrationOnboardingPage/slides/CompleteSlide'
import { ConnectionFailedSlide } from '@pages/BankIntegrationOnboardingPage/slides/ConnectionFailedSlide'
import { FinalizeSlide } from '@pages/BankIntegrationOnboardingPage/slides/FinalizeSlide'
import {
  SlideContent,
  SlideText,
  SlideTitle
} from '@pages/BankIntegrationOnboardingPage/slides/slide.styled'
import { motion } from 'framer-motion'
import React, { useMemo } from 'react'
import { Route, Switch, useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { HolviEditSlide } from '@pages/BankIntegrationOnboardingPage/slides/HolviEditSlide.tsx'
import { useTranslation } from 'react-i18next'
import { RedirectNotice } from '@root/containers/RedirectNotice'
import {
  clearGetStartedBankHandoverPending,
  rememberGetStartedBankReturnKind,
  shouldLetGetStartedHandleBankReturn,
  type GetStartedBankReturnKind
} from '@containers/GetStartedWidget/useBankHandover'
import { invalidateFirstTransactionFetchNotice } from '@query'
import { useQueryClient } from '@tanstack/react-query'

interface Props {
  baseUrl: string
}

export const BankIntegrationOnboardingPage: React.FC<Props> = ({ baseUrl }) => {
  const history = useHistory()
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { isEmbedded, postMessage } = useMobileEmbed()
  const businessUrl = useMemo(() => `/${slug}`, [slug])
  const permissionsLoaded = usePermissionsLoaded()
  const canManageIntegrations = usePermission(PermissionSet.INTEGRATION_ADMIN)
  const { enabled: holviBusinessAccountsEnabled } = useFeature('holvi_business_accounts')

  const finishConnection = (kind: GetStartedBankReturnKind, onOpenedTransactions: () => void) => {
    const isHolvi = kind === 'holvi'

    if (isEmbedded || !shouldLetGetStartedHandleBankReturn(slug)) {
      showCompleteToast({
        title: t(isHolvi ? 'bankOnboarding.complete.holviTitle' : 'bankOnboarding.complete.title'),
        description: t(
          isHolvi
            ? 'bankOnboarding.complete.holviDescription'
            : 'bankOnboarding.complete.description1'
        ),
        descriptionContinued: t(
          isHolvi
            ? 'bankOnboarding.complete.holviDescription2'
            : 'bankOnboarding.complete.description2'
        ),
        actionLabel: isEmbedded ? undefined : t('bankOnboarding.complete.goToTransactions'),
        onAction: isEmbedded
          ? undefined
          : () => {
              onOpenedTransactions()
              history.push(getDocumentsPageUrl(slug))
            },
        redirect: isEmbedded && (
          <RedirectNotice
            onRedirect={() =>
              postMessage(
                isHolvi
                  ? MobileEmbedMessages.HOLVI_ONBOARD_COMPLETE
                  : MobileEmbedMessages.BANK_ONBOARD_COMPLETE
              )
            }
          />
        )
      })
      return
    }

    rememberGetStartedBankReturnKind(slug, kind)
  }

  const leaveConnectionFlow = async (kind: GetStartedBankReturnKind) => {
    let openedTransactions = false
    finishConnection(kind, () => {
      openedTransactions = true
    })

    if (isEmbedded) {
      return
    }

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [slug], exact: true }),
      invalidateFirstTransactionFetchNotice(queryClient, slug)
    ])
    if (!openedTransactions) {
      history.push(businessUrl)
    }
  }

  const urls = useMemo(
    () => ({
      START: `${baseUrl}/start`,
      PARTNERS: `${baseUrl}/partners`,
      ASPSPS: `${baseUrl}/aspsps`,
      EDIT: `${baseUrl}/edit`,
      HOLVI_EDIT: `${baseUrl}/holvi-edit`,
      HOLVI_FINALIZE: `${baseUrl}/holvi-finalize`,
      FINALIZE: `${baseUrl}/finalize`,
      FAILED: `${baseUrl}/failed`,
      COMPLETE: `${baseUrl}/complete`
    }),
    [baseUrl]
  )

  // Every step of this flow reads or writes bank integrations, which require
  // integration_admin, so entering it without that permission only yields 403s.
  // usePermission is false until permissions arrive, hence the loaded check.
  if (permissionsLoaded && !canManageIntegrations) {
    return (
      <StyledPage>
        <StyledPageContent>
          <SlideContent>
            <SlideTitle>{t('bankOnboarding.noAccess.title')}</SlideTitle>
            <SlideText>{t('bankOnboarding.noAccess.description')}</SlideText>
          </SlideContent>
        </StyledPageContent>
      </StyledPage>
    )
  }

  return (
    <StyledPage>
      <StyledPageContent>
        <Switch>
          <Route path={urls.START}>
            <EnsureBankAccountSlide
              onHasBankAccount={() => history.push(urls.ASPSPS)}
              onHasNoBankAccount={() =>
                history.push(holviBusinessAccountsEnabled ? urls.PARTNERS : urls.ASPSPS)
              }
              onSkip={() => {
                clearGetStartedBankHandoverPending(slug)
                history.push(businessUrl)
              }}
            />
          </Route>

          <Route path={urls.PARTNERS}>
            <PartnerSlide
              onDone={() => {
                clearGetStartedBankHandoverPending(slug)
                history.push(businessUrl)
              }}
              goBack={() => history.push(urls.START)}
            />
          </Route>

          <Route path={urls.ASPSPS}>
            <AspspsSlide
              goBack={() => {
                if (isEmbedded) {
                  postMessage(MobileEmbedMessages.BANK_ONBOARD_COMPLETE)
                  return
                }
                clearGetStartedBankHandoverPending(slug)
                history.goBack()
              }}
            />
          </Route>

          <Route path={urls.FAILED}>
            <ConnectionFailedSlide
              onBack={() => {
                clearGetStartedBankHandoverPending(slug)
                if (isEmbedded) {
                  history.replace(
                    getMobileViewUrls(slug, MobileViewPath.SETTINGS_BANK_INTEGRATIONS)
                  )
                  return
                }
                history.replace(getSettingsPageUrl(slug, { page: 'integrations' }))
              }}
              onPickAnotherBank={() => history.replace(urls.ASPSPS)}
            />
          </Route>

          <Route path={urls.FINALIZE}>
            <FinalizeSlide
              isEdit={false}
              onDone={() => leaveConnectionFlow('enableBanking')}
              goBack={() => history.push(urls.ASPSPS)}
            />
          </Route>

          <Route path={urls.EDIT}>
            <FinalizeSlide
              isEdit={true}
              onDone={() => {
                isEmbedded
                  ? history.push(getMobileViewUrls(slug, MobileViewPath.SETTINGS_BANK_INTEGRATIONS))
                  : history.push(getSettingsPageUrl(slug, { page: 'integrations' }))
              }}
              goBack={() => history.goBack()}
            />
          </Route>

          <Route path={urls.HOLVI_EDIT}>
            <HolviEditSlide
              isEdit={true}
              onDone={() => {
                isEmbedded
                  ? history.push(getMobileViewUrls(slug, MobileViewPath.SETTINGS_BANK_INTEGRATIONS))
                  : history.push(getSettingsPageUrl(slug, { page: 'holvi' }))
              }}
              goBack={() => {
                isEmbedded
                  ? history.goBack()
                  : history.push(getSettingsPageUrl(slug, { page: 'holvi' }))
              }}
            />
          </Route>

          <Route path={urls.HOLVI_FINALIZE}>
            <HolviEditSlide
              isEdit={false}
              onDone={() => leaveConnectionFlow('holvi')}
              goBack={() => {
                isEmbedded
                  ? history.push(getMobileViewUrls(slug, MobileViewPath.SETTINGS_BANK_INTEGRATIONS))
                  : history.push(getSettingsPageUrl(slug, { page: 'holvi' }))
              }}
            />
          </Route>
        </Switch>
      </StyledPageContent>
    </StyledPage>
  )
}

const StyledPage = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.colors.neutralGray};
  padding: ${({ theme }) => theme.spacing.xl}rem;

  @media (max-width: 1024px) {
    padding: ${({ theme }) => theme.spacing.sm}rem;
  }

  @media (max-width: 768px) {
    padding: 0;
  }
`

const StyledPageContent = styled(motion.div).attrs({
  initial: { opacity: 0 },
  animate: { opacity: 1 }
})`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-height: 1000px;
  max-width: 600px;
  padding: ${({ theme }) => theme.spacing.xl}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  overflow: auto;

  @media (max-width: 1024px) {
    padding: ${({ theme }) => theme.spacing.md}rem;
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.md}rem;
    max-width: 100%;
    max-height: 100%;
  }
`
